import { useEffect, useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { SectionLabel } from "@/components/Editorial";

import { API } from "@/lib/api";
const ax = axios.create({ baseURL: API, withCredentials: true });

const fmtErr = (d) => {
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || "").join(" ");
  return "Something went wrong. Please try again.";
};

const COPY = {
  en: {
    label: "Maison Core Staff",
    loginTitle: "Private intakes.",
    loginLead: "Sign in to review submitted Private Pilates intakes.",
    email: "Email",
    password: "Password",
    signIn: "Sign in",
    signingIn: "Signing in…",
    listTitle: "Private intakes.",
    corpTitle: "Corporate enquiries.",
    tabIntakes: "Private intakes",
    tabCorporate: "Corporate",
    corpEmpty: "No corporate enquiries yet.",
    corpSections: { contact: "Contact", request: "The request" },
    corpFields: { email: "Email", phone: "Phone", orgType: "Type of organisation", participants: "Participants", interest: "Interested in", date: "Preferred date", time: "Preferred time", frequency: "Frequency", message: "Message", language: "Language", submitted_at: "Submitted" },
    interestMap: { team_session: "Private team Reformer Pilates", wellness: "Corporate wellness / recurring", event: "Team or company event", tailored: "Tailored experience", not_sure: "Not sure yet" },
    freqMap: { one_off: "One-off", recurring: "Recurring", not_sure: "Not sure yet" },
    searchPh: "Search by name or email…",
    noResults: "No intakes match your search.",
    client: "Client",
    submitted: "Submitted",
    intakeStatus: "Status",
    statusNew: "New",
    statusReviewed: "Reviewed",
    empty: "No intakes have been submitted yet.",
    back: "Back to overview",
    logout: "Sign out",
    markReviewed: "Mark as reviewed",
    markNew: "Mark as new",
    yes: "Yes",
    no: "No",
    preferNot: "Prefer not to say",
    sections: { about: "About the client", practice: "Their practice", health: "Good to know", confirm: "Confirmation" },
    fields: {
      date_of_birth: "Date of birth", email: "Email", phone: "Phone",
      experience: "Pilates experience", focus: "Focus areas", focus_other: "Other focus", goals_notes: "Goals",
      complaints: "Pain / injuries / complaints", avoid: "Movements to avoid", treatment: "Surgery / treatment", pregnancy: "Pregnancy / recently given birth",
      other_notes: "Other notes", digital_signature: "Digital confirmation", language: "Language", submitted_at: "Submitted",
    },
    experienceMap: { first_time: "First time", mat: "Mat Pilates", reformer: "Reformer Pilates", both: "Mat & Reformer" },
    focusMap: { strength: "Strength", core: "Core stability", posture: "Posture", mobility: "Mobility & flexibility", technique: "Technique", fullbody: "Full-body", attention: "Personal attention", other: "Other" },
  },
  nl: {
    label: "Maison Core Staff",
    loginTitle: "Private intakes.",
    loginLead: "Log in om ingezonden Private Pilates intakes te bekijken.",
    email: "E-mailadres",
    password: "Wachtwoord",
    signIn: "Inloggen",
    signingIn: "Inloggen…",
    listTitle: "Private intakes.",
    corpTitle: "Corporate aanvragen.",
    tabIntakes: "Private intakes",
    tabCorporate: "Corporate",
    corpEmpty: "Nog geen corporate aanvragen.",
    corpSections: { contact: "Contact", request: "De aanvraag" },
    corpFields: { email: "E-mail", phone: "Telefoon", orgType: "Type organisatie", participants: "Deelnemers", interest: "Geïnteresseerd in", date: "Gewenste datum", time: "Gewenste tijd", frequency: "Frequentie", message: "Bericht", language: "Taal", submitted_at: "Ingezonden" },
    interestMap: { team_session: "Privé team Reformer Pilates", wellness: "Corporate wellness / terugkerend", event: "Team- of bedrijfsevent", tailored: "Ervaring op maat", not_sure: "Nog niet zeker" },
    freqMap: { one_off: "Eenmalig", recurring: "Terugkerend", not_sure: "Nog niet zeker" },
    searchPh: "Zoek op naam of e-mail…",
    noResults: "Geen intakes gevonden voor deze zoekopdracht.",
    client: "Client",
    submitted: "Ingezonden",
    intakeStatus: "Status",
    statusNew: "Nieuw",
    statusReviewed: "Bekeken",
    empty: "Er zijn nog geen intakes ingezonden.",
    back: "Terug naar overzicht",
    logout: "Uitloggen",
    markReviewed: "Markeer als bekeken",
    markNew: "Markeer als nieuw",
    yes: "Ja",
    no: "Nee",
    preferNot: "Zegt het liever niet",
    sections: { about: "Over de client", practice: "Hun practice", health: "Goed om te weten", confirm: "Bevestiging" },
    fields: {
      date_of_birth: "Geboortedatum", email: "E-mail", phone: "Telefoon",
      experience: "Pilates-ervaring", focus: "Focusgebieden", focus_other: "Andere focus", goals_notes: "Doelen",
      complaints: "Pijn / blessures / klachten", avoid: "Te vermijden bewegingen", treatment: "Operatie / behandeling", pregnancy: "Zwangerschap / recent bevallen",
      other_notes: "Overige opmerkingen", digital_signature: "Digitale bevestiging", language: "Taal", submitted_at: "Ingezonden",
    },
    experienceMap: { first_time: "Eerste keer", mat: "Mat Pilates", reformer: "Reformer Pilates", both: "Mat & Reformer" },
    focusMap: { strength: "Kracht", core: "Core-stabiliteit", posture: "Houding", mobility: "Mobiliteit & flexibiliteit", technique: "Techniek", fullbody: "Full-body", attention: "Persoonlijke aandacht", other: "Anders" },
  },
};

const field = "w-full border-b border-onyx/25 bg-transparent py-4 font-sans text-lg text-onyx placeholder:text-onyx/40 focus:border-onyx focus:outline-none transition-colors";
const labelCls = "label text-terracotta";

const fmtDate = (iso, lang) => {
  try {
    return new Date(iso).toLocaleString(lang === "nl" ? "nl-NL" : "en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

const Row = ({ label, value }) => (
  <div className="flex flex-col gap-1 border-b border-onyx/10 py-4 md:flex-row md:items-baseline md:gap-8">
    <span className="label w-56 shrink-0 text-onyx/50">{label}</span>
    <span className="font-sans text-base text-onyx/85 whitespace-pre-wrap">{value || "—"}</span>
  </div>
);

export default function Staff() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;
  const [auth, setAuth] = useState(null); // null=checking, false=login, object=staff
  const [tab, setTab] = useState("intakes");
  const [intakes, setIntakes] = useState([]);
  const [corp, setCorp] = useState([]);
  const [detail, setDetail] = useState(null);
  const [corpDetail, setCorpDetail] = useState(null);
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => { document.head.removeChild(m); };
  }, []);

  const authedGet = async (url) => {
    try {
      return (await ax.get(url)).data;
    } catch (e) {
      if (e.response?.status === 401) {
        await ax.post("/auth/refresh");
        return (await ax.get(url)).data;
      }
      throw e;
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const me = await authedGet("/auth/me");
        setAuth(me);
      } catch {
        setAuth(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!auth) return;
    const params = new URLSearchParams(window.location.search);
    authedGet("/staff/intakes")
      .then((list) => {
        setIntakes(list);
        const wanted = params.get("intake");
        if (wanted && list.some((i) => i.id === wanted)) openIntake(wanted);
      })
      .catch(() => setAuth(false));
    authedGet("/staff/corporate")
      .then((list) => {
        setCorp(list);
        const wanted = params.get("corporate");
        if (wanted && list.some((i) => i.id === wanted)) { setTab("corporate"); openCorporate(wanted); }
      })
      .catch(() => {});
  }, [auth]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await ax.post("/auth/login", creds);
      setAuth(data);
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await ax.post("/auth/logout").catch(() => {});
    setAuth(false);
    setDetail(null);
    setCorpDetail(null);
    setIntakes([]);
    setCorp([]);
  };

  const openCorporate = async (id) => {
    try {
      setCorpDetail(await authedGet(`/staff/corporate/${id}`));
      window.scrollTo(0, 0);
    } catch {
      toast.error("Could not load enquiry.");
    }
  };

  const setCorpStatus = async (status) => {
    try {
      await ax.patch(`/staff/corporate/${corpDetail.id}/status`, { status });
      setCorpDetail({ ...corpDetail, status });
      setCorp(corp.map((i) => (i.id === corpDetail.id ? { ...i, status } : i)));
    } catch {
      toast.error("Could not update status.");
    }
  };

  const openIntake = async (id) => {
    try {
      setDetail(await authedGet(`/staff/intakes/${id}`));
      window.scrollTo(0, 0);
    } catch {
      toast.error("Could not load intake.");
    }
  };

  const setStatus = async (status) => {
    try {
      await ax.patch(`/staff/intakes/${detail.id}/status`, { status });
      setDetail({ ...detail, status });
      setIntakes(intakes.map((i) => (i.id === detail.id ? { ...i, status } : i)));
    } catch {
      toast.error("Could not update status.");
    }
  };

  const ynLabel = (v) => (v ? c.yes : c.no);
  const pregnancyLabel = (v) => (v === "yes" ? c.yes : v === "prefer_not_to_say" ? c.preferNot : c.no);

  if (auth === null) {
    return <section className="bg-petal"><div className="mx-auto max-w-3xl px-6 py-40 md:px-12"><p className="label text-onyx/40">…</p></div></section>;
  }

  // LOGIN
  if (auth === false) {
    return (
      <>
        <Toaster position="top-center" richColors />
        <section className="bg-petal min-h-[70vh]" data-testid="staff-login">
          <div className="mx-auto max-w-xl px-6 pb-24 pt-32 md:px-12 md:pt-40">
            <SectionLabel>{c.label}</SectionLabel>
            <h1 className="mt-8 font-serif text-5xl md:text-6xl">{c.loginTitle}</h1>
            <p className="mt-5 font-sans text-base text-onyx/65">{c.loginLead}</p>
            <form onSubmit={login} className="mt-12 space-y-8">
              <div>
                <label className={labelCls}>{c.email}</label>
                <input type="email" required value={creds.email} onChange={(e) => setCreds({ ...creds, email: e.target.value })} className={field} data-testid="staff-email" />
              </div>
              <div>
                <label className={labelCls}>{c.password}</label>
                <input type="password" required value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} className={field} data-testid="staff-password" />
              </div>
              <button type="submit" disabled={busy} className="label border border-onyx bg-onyx px-10 py-5 text-petal transition-colors duration-500 hover:bg-transparent hover:text-onyx disabled:opacity-50" data-testid="staff-login-submit">
                {busy ? c.signingIn : c.signIn}
              </button>
            </form>
          </div>
        </section>
      </>
    );
  }

  // DETAIL
  if (detail) {
    const d = detail;
    return (
      <>
        <Toaster position="top-center" richColors />
        <section className="bg-petal min-h-screen" data-testid="staff-detail">
          <div className="mx-auto max-w-4xl px-6 pb-24 pt-32 md:px-12 md:pt-40">
            <button onClick={() => setDetail(null)} className="label border-b border-onyx/40 pb-1 text-onyx/60 hover:text-onyx" data-testid="staff-detail-back">← {c.back}</button>
            <div className="mt-10 flex flex-wrap items-end justify-between gap-6">
              <div>
                <SectionLabel>{c.label}</SectionLabel>
                <h1 className="mt-6 font-serif text-5xl md:text-6xl">{d.full_name}</h1>
                <p className="mt-3 label text-onyx/50">{fmtDate(d.created_at, lang)} · {d.intake_type}</p>
              </div>
              <button
                onClick={() => setStatus(d.status === "reviewed" ? "new" : "reviewed")}
                className={`label border px-8 py-4 transition-colors duration-300 ${d.status === "reviewed" ? "border-onyx/30 text-onyx/60 hover:border-onyx hover:text-onyx" : "border-onyx bg-onyx text-petal hover:bg-transparent hover:text-onyx"}`}
                data-testid="staff-status-toggle"
              >
                {d.status === "reviewed" ? c.markNew : c.markReviewed}
              </button>
            </div>

            <div className="mt-14 space-y-12">
              <div>
                <SectionLabel>{c.sections.about}</SectionLabel>
                <div className="mt-4">
                  <Row label={c.fields.date_of_birth} value={d.date_of_birth} />
                  <Row label={c.fields.email} value={d.email} />
                  <Row label={c.fields.phone} value={d.phone} />
                </div>
              </div>
              <div>
                <SectionLabel>{c.sections.practice}</SectionLabel>
                <div className="mt-4">
                  <Row label={c.fields.experience} value={c.experienceMap[d.experience] || d.experience} />
                  <Row label={c.fields.focus} value={(d.focus_areas || []).map((k) => c.focusMap[k] || k).join(", ")} />
                  {d.focus_other && <Row label={c.fields.focus_other} value={d.focus_other} />}
                  <Row label={c.fields.goals_notes} value={d.goals_notes} />
                </div>
              </div>
              <div>
                <SectionLabel>{c.sections.health}</SectionLabel>
                <div className="mt-4">
                  <Row label={c.fields.complaints} value={d.has_complaints ? `${c.yes} — ${d.complaints_detail || ""}` : ynLabel(d.has_complaints)} />
                  <Row label={c.fields.avoid} value={d.avoids_movements ? `${c.yes} — ${d.avoid_detail || ""}` : ynLabel(d.avoids_movements)} />
                  <Row label={c.fields.treatment} value={d.has_treatment ? `${c.yes} — ${d.treatment_detail || ""}` : ynLabel(d.has_treatment)} />
                  <Row label={c.fields.pregnancy} value={d.pregnancy === "yes" ? `${c.yes}${d.pregnancy_detail ? ` — ${d.pregnancy_detail}` : ""}` : pregnancyLabel(d.pregnancy)} />
                  <Row label={c.fields.other_notes} value={d.other_notes} />
                </div>
              </div>
              <div>
                <SectionLabel>{c.sections.confirm}</SectionLabel>
                <div className="mt-4">
                  <Row label={c.fields.digital_signature} value={d.digital_signature} />
                  <Row label={c.fields.language} value={(d.language || "en").toUpperCase()} />
                  <Row label={c.fields.submitted_at} value={fmtDate(d.created_at, lang)} />
                </div>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  // CORPORATE DETAIL
  if (corpDetail) {
    const d = corpDetail;
    return (
      <>
        <Toaster position="top-center" richColors />
        <section className="bg-petal min-h-screen" data-testid="staff-corp-detail">
          <div className="mx-auto max-w-4xl px-6 pb-24 pt-32 md:px-12 md:pt-40">
            <button onClick={() => setCorpDetail(null)} className="label border-b border-onyx/40 pb-1 text-onyx/60 hover:text-onyx" data-testid="staff-corp-detail-back">← {c.back}</button>
            <div className="mt-10 flex flex-wrap items-end justify-between gap-6">
              <div>
                <SectionLabel>{c.label}</SectionLabel>
                <h1 className="mt-6 font-serif text-5xl md:text-6xl">{d.company}</h1>
                <p className="mt-3 label text-onyx/50">{fmtDate(d.created_at, lang)}</p>
              </div>
              <button
                onClick={() => setCorpStatus(d.status === "reviewed" ? "new" : "reviewed")}
                className={`label border px-8 py-4 transition-colors duration-300 ${d.status === "reviewed" ? "border-onyx/30 text-onyx/60 hover:border-onyx hover:text-onyx" : "border-onyx bg-onyx text-petal hover:bg-transparent hover:text-onyx"}`}
                data-testid="staff-corp-status-toggle"
              >
                {d.status === "reviewed" ? c.markNew : c.markReviewed}
              </button>
            </div>
            <div className="mt-14 space-y-12">
              <div>
                <SectionLabel>{c.corpSections.contact}</SectionLabel>
                <div className="mt-4">
                  <Row label={c.client} value={`${d.first_name} ${d.last_name}`} />
                  <Row label={c.corpFields.email} value={d.email} />
                  <Row label={c.corpFields.phone} value={d.phone} />
                  <Row label={c.corpFields.orgType} value={d.organisation_type} />
                </div>
              </div>
              <div>
                <SectionLabel>{c.corpSections.request}</SectionLabel>
                <div className="mt-4">
                  <Row label={c.corpFields.interest} value={c.interestMap[d.interest] || d.interest} />
                  <Row label={c.corpFields.participants} value={d.participants} />
                  <Row label={c.corpFields.date} value={d.preferred_date} />
                  <Row label={c.corpFields.time} value={d.preferred_time} />
                  <Row label={c.corpFields.frequency} value={d.frequency ? (c.freqMap[d.frequency] || d.frequency) : null} />
                  <Row label={c.corpFields.message} value={d.message} />
                  <Row label={c.corpFields.language} value={(d.language || "en").toUpperCase()} />
                  <Row label={c.corpFields.submitted_at} value={fmtDate(d.created_at, lang)} />
                </div>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  // LIST
  const q = query.trim().toLowerCase();
  const source = tab === "corporate" ? corp : intakes;
  const filtered = q
    ? source.filter((i) => (i.full_name || `${i.first_name} ${i.last_name}` || "").toLowerCase().includes(q) || (i.company || "").toLowerCase().includes(q) || (i.email || "").toLowerCase().includes(q))
    : source;
  return (
    <>
      <Toaster position="top-center" richColors />
      <section className="bg-petal min-h-screen" data-testid="staff-list">
        <div className="mx-auto max-w-5xl px-6 pb-24 pt-32 md:px-12 md:pt-40">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <SectionLabel>{c.label}</SectionLabel>
              <h1 className="mt-8 font-serif text-5xl md:text-6xl">{tab === "corporate" ? c.corpTitle : c.listTitle}</h1>
            </div>
            <button onClick={logout} className="label border-b border-onyx/40 pb-1 text-onyx/60 hover:text-onyx" data-testid="staff-logout">{c.logout}</button>
          </div>

          <div className="mt-10 inline-flex border border-onyx/25 p-1">
            {[["intakes", c.tabIntakes, intakes.length], ["corporate", c.tabCorporate, corp.length]].map(([key, lbl, count]) => (
              <button
                key={key}
                onClick={() => { setTab(key); setQuery(""); }}
                className={`label px-6 py-3 transition-colors duration-300 md:px-8 ${tab === key ? "bg-onyx text-petal" : "text-onyx/55 hover:text-onyx"}`}
                data-testid={`staff-tab-${key}`}
                aria-pressed={tab === key}
              >
                {lbl} ({count})
              </button>
            ))}
          </div>

          <div className="mt-10">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={c.searchPh}
              className="mb-10 w-full max-w-md border-b border-onyx/25 bg-transparent py-4 font-sans text-lg text-onyx placeholder:text-onyx/40 transition-colors focus:border-onyx focus:outline-none"
              data-testid="staff-search"
            />
            <div className="hidden grid-cols-12 border-b border-onyx/20 pb-4 md:grid">
              <span className="label col-span-6 text-onyx/50">{c.client}</span>
              <span className="label col-span-4 text-onyx/50">{c.submitted}</span>
              <span className="label col-span-2 text-onyx/50">{c.intakeStatus}</span>
            </div>
            {source.length === 0 && <p className="mt-10 font-sans text-base text-onyx/55" data-testid="staff-empty">{tab === "corporate" ? c.corpEmpty : c.empty}</p>}
            {source.length > 0 && filtered.length === 0 && <p className="mt-10 font-sans text-base text-onyx/55" data-testid="staff-no-results">{c.noResults}</p>}
            {filtered.map((i) => (
              <button
                key={i.id}
                onClick={() => (tab === "corporate" ? openCorporate(i.id) : openIntake(i.id))}
                className="grid w-full grid-cols-1 gap-1 border-b border-onyx/10 py-5 text-left transition-colors hover:bg-blush/30 md:grid-cols-12 md:items-baseline md:gap-0"
                data-testid={`staff-${tab === "corporate" ? "corp" : "intake"}-row-${i.id}`}
              >
                <span className="col-span-6 font-serif text-2xl">
                  {tab === "corporate" ? i.company : i.full_name}
                  {tab === "corporate" && <span className="ml-3 font-sans text-sm text-onyx/50">{i.first_name} {i.last_name}</span>}
                </span>
                <span className="col-span-4 font-sans text-sm text-onyx/60">{fmtDate(i.created_at, lang)}</span>
                <span className={`label col-span-2 ${i.status === "reviewed" ? "text-onyx/40" : "text-terracotta"}`}>
                  {i.status === "reviewed" ? c.statusReviewed : c.statusNew}
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
